import { createAction, props } from '@ngrx/store';
import {
    NotificationCompletionState,
    NotificationState,
    NotificationStatusModelFields,
    NotificationStatusRecordState,
} from '../notification-collection.state';
import { NotificationLongTextLineState } from '../notification-entities.state';
import { NotificationIdState } from '../../utilities/notification-id.type';

/**
 * Dispatched by `NotificationNgrxService.init()` (and by `refreshRequested$` / `syncCrossDevice$`)
 * to (re-)fetch notifications from OData. `isAppStart` travels on to `notificationLoaded` and ends
 * up on the slice's `lastLoad` marker, where it keeps the app-start load out of the "notifications
 * updated" stream: on the first load there is nothing for its readers to react to yet.
 */
export const initNotification = createAction('[Notification] Init', props<{ isAppStart: boolean }>());

/**
 * Dispatched by `init$` once the backend fetch (and the address work that rides along with it) has
 * completed, with the entries exactly as they were converted from the payloads.
 *
 * Everything that turns those into the collection the slice holds is the reducer's — the recorded
 * status of the notifications that are still there carried over onto them (which is also what drops
 * the status of the ones the backend no longer sends), the reconciliation against what the backend
 * now says, the pause invariant, and the ordering. See `setLoadedReducer`.
 *
 * `allowNotificationPause` rides along because the pause invariant depends on it and a reducer
 * cannot read the config slice; `init$` reads it at dispatch time.
 */
export const notificationLoaded = createAction(
    '[Notification] Loaded',
    props<{ entries: NotificationState[]; isAppStart: boolean; allowNotificationPause: boolean }>(),
);

/**
 * Dispatched by `init$` when the OData fetch (or the work chained after it) fails. The message is
 * formatted at the dispatch site rather than in the reducer: what arrives there is an unknown
 * rejection value, and formatting one can throw — which a reducer must never do.
 */
export const notificationFailed = createAction('[Notification] Failed', props<{ error: string }>());

/**
 * Dispatched when a storage sync of the slice fails: the persist could not serialize or write the
 * slice, or the read back delivered something the slice cannot restore from. Records the failure on
 * `NotificationCollectionState.error` without touching `loadStatus` — see that field's doc comment.
 */
export const notificationStorageSyncError = createAction(
    '[Notification] Storage sync error',
    props<{ error: string }>(),
);

/**
 * Requests a read of the persisted state, independently of a fetch. `initNotification` already
 * triggers one on the first call; this is for the cross-device sync, which has to re-read what the
 * sync just wrote into the concurrency store before the re-fetch reconciles against it.
 */
export const hydrateNotifications = createAction('[Notification] Hydrate');

/**
 * Dispatched by `applyHydratedNotification$` once the generic persistence layer has read the
 * cache back. Unlike `notificationLoaded`, this never touches `loadStatus` — hydration is a warm
 * cache, never a substitute for the `init()` fetch that is already in flight (`initNotification`
 * already set `loadStatus` to `Loading` before hydration runs).
 */
export const notificationRestoredFromStorage = createAction(
    '[Notification] Restored from storage',
    props<{ entries: NotificationState[] }>(),
);

/** Replace all entries at once. The reducer re-sorts by notification start. */
export const setNotificationEntries = createAction(
    '[Notification] Set entries',
    props<{ entries: NotificationState[] }>(),
);

/** Record the notification ids the backend sent that failed to parse. Ephemeral, reset every load. */
export const setIncorrectNotifications = createAction(
    '[Notification] Set incorrect notifications',
    props<{ ids: string[] }>(),
);

/**
 * Requests a re-fetch of notifications from anywhere in the app that knows the list may be stale.
 * Handled by `refreshRequested$`, which ignores it until the slice holds notifications at all.
 */
export const notificationRefreshRequired = createAction('[Notification] Refresh required');

/**
 * Requests the pause/unpause redistribution across in-progress notifications. Dispatched by
 * `init$` after every load, and by `NotificationNgrxService.handlePausedNotifications()` for
 * direct callers.
 */
export const handlePausedNotifications = createAction('[Notification] Handle paused notifications');

/**
 * Requests the missing-equipment warning / offline-store-recreation check. With
 * `showAppNotification: false` the check still recreates the offline store when it has to, but stays
 * silent — a first load has nothing to warn about yet.
 */
export const showNoEquipmentNotificationList = createAction(
    '[Notification] Show no-equipment notification list',
    props<{ showAppNotification: boolean }>(),
);

/**
 * Sets the notification whose status `selectCurrentNotificationStatus` reports. `id` is ephemeral
 * UI-session state, never persisted (see `NotificationCollectionState.currentNotificationId`).
 */
export const setCurrentNotification = createAction(
    '[Notification] Set current notification',
    props<{ id: NotificationIdState }>(),
);

/**
 * Merge `changes` into a notification's status fields. Ignored when the id is unknown: the status
 * of a notification the store does not hold has nowhere to live, and the next load would discard
 * it anyway.
 */
export const updateNotificationStatusFields = createAction(
    '[Notification] Update status fields',
    props<{
        id: NotificationIdState;
        changes: {
            escalated?: boolean;
            cancelled?: string;
            unread?: boolean;
            paused?: boolean;
        };
    }>(),
);

/**
 * Append a single status record to a notification. Idempotent — a status code the notification
 * already carries is ignored — and, like {@link updateNotificationStatusFields}, a no-op for an
 * unknown id.
 */
export const addStatusToNotificationEntry = createAction(
    '[Notification] Add status to entry',
    props<{ id: NotificationIdState; status: NotificationStatusRecordState }>(),
);

/**
 * Replace the status fields of the listed notifications wholesale. The bulk counterpart of
 * {@link updateNotificationStatusFields}, for `NotificationStatusInterface.setNotificationStatusList`;
 * notifications the list does not mention keep the status they have, and the two fields the legacy
 * model cannot carry (the cancellation confirmation and the completion record) are left alone on
 * the ones it does mention.
 */
export const setNotificationStatusFields = createAction(
    '[Notification] Set status fields',
    props<{ entries: Array<{ id: NotificationIdState; fields: NotificationStatusModelFields }> }>(),
);

/**
 * Replace every notification's completion record with what the list says: the ones it names are
 * marked completed, the ones it does not are marked not completed. The bulk counterpart of
 * {@link markNotificationCompleted}, for `NotificationStatusInterface.saveRecentlyCompletedNotifications`.
 */
export const setNotificationCompletions = createAction(
    '[Notification] Set completions',
    props<{ completions: Array<{ id: NotificationIdState; completion: NotificationCompletionState }> }>(),
);

/**
 * Record that a notification's confirmation was completed. Like the other entry-targeted writes, a
 * no-op for an unknown id — a completion record needs the notification it belongs to.
 */
export const markNotificationCompleted = createAction(
    '[Notification] Mark completed',
    props<{ id: NotificationIdState; completion: NotificationCompletionState }>(),
);

/**
 * Discard the completion records that have aged out, and with them the entries retained by one. The
 * cutoff is derived in the reducer from {@link MAX_COMPLETED_NOTIFICATION_AGE_MS} so that the
 * retention policy lives in the NgRx layer rather than in every caller.
 */
export const removeExpiredCompletedNotifications = createAction('[Notification] Remove expired completed');

/** Record that the user has confirmed the cancellation of a notification. */
export const cancellationConfirmationSaved = createAction(
    '[Notification] Cancellation confirmation saved',
    props<{ id: NotificationIdState }>(),
);

/**
 * Clear the cancellation confirmation of every notification that carries one. Takes no id because
 * the legacy `removeCancellationConfirmationForInprogress()` contract has none: it cleared the one
 * slot the confirmation used to live in, which the flag on each notification stands in for now.
 */
export const cancellationConfirmationRemoved = createAction('[Notification] Cancellation confirmation removed');

/**
 * Requests a write of the slice to storage without changing it. For callers of the legacy
 * `saveNotificationStatusList()`, which expects to be able to flush the collection on demand;
 * every mutating action already persists on its own.
 */
export const persistNotificationsRequested = createAction('[Notification] Persist requested');

/**
 * Announces that the user cancelled a notification and the backend accepted it. Dispatched by the
 * cancellation dialog, which is the one change to the list that no load reports: the dialog's own
 * writes land on the slice, but nothing else tells the dashboard and the detail screens that the
 * list they show is stale. Readers hear it through `notificationsUpdated$()`.
 */
export const notificationCancelled = createAction('[Notification] Cancelled');

/**
 * Overwrites a notification's internal-info long-text lines wholesale. Dispatched by
 * `NotificationDetailsModalsService.openInternalInfo()` once the internal-info modal has saved the
 * edits it made on the legacy `InternalInfoModel` instance, so the slice reflects them too — without
 * this, the next `NotificationModelFactory.create()` rebuilds a fresh `InternalInfoModel` from the
 * slice and the in-memory edits are lost. Like the other entry-targeted writes, a no-op for an
 * unknown id — see {@link updateNotificationStatusFields}.
 */
export const updateNotificationInternalInfoLines = createAction(
    '[Notification] Update internal info lines',
    props<{ id: NotificationIdState; internalInfoLines: NotificationLongTextLineState[] }>(),
);
